import React from 'react';
import { Icons, Stack, Text, styled, useTheme } from '@/theme/index';
import { commaFormatCurrency } from '@/utils/commaFormat/commaFormatCurrency';
import { useScreenSize } from '@/utils/useScreenSize';
import { PartnersVisualizationProps, PartnersProps } from '../EstimatedValue';

const StyledScoreStack = styled(Stack)`
  padding: 8px 0;
  border-top: 2px solid #e2e2e2;

  &:last-child {
    border-bottom: 1px solid #e2e2e2;
  }
`;

const ValueStack = styled(Stack)(({ theme }) => ({
  paddingLeft: '10px',
  minWidth: '100px',
  borderLeft: `1px solid ${theme.palette.secondary.light}`,

  [theme.breakpoints.up('sm')]: {
    minWidth: '200px',
    paddingLeft: '50px',
    paddingRight: '50px',
  },
}));

const ConfidenceScoreBar = styled('div')`
  width: 60px;
  height: 4px;
  background-color: #e2e2e2;
`;

const ConfidenceScorePercent = styled('div')(({ theme }) => ({
  height: '100%',
  backgroundImage: `linear-gradient(90deg, ${theme.palette.progress?.main} 0%, ${theme.palette.progress?.light} 51.04%, ${theme.palette.progress?.dark} 100%)`,
}));

const StyledPartnerName = styled(Text)(({ theme }) => ({
  paddingLeft: theme.spacing(1.25),
  minHeight: '46px',
  marginBottom: theme.spacing(0),
  borderLeft: '3px solid transparent',
  width: '130px',
  [theme.breakpoints.up('sm')]: {
    width: 'unset',
  },
}));

const StylesForTableArrow = {
  width: '16px',
  height: '16px',
  position: 'absolute',
  marginTop: '2px',
};

function getTableNodeWithDirection(direction: string, total: number) {
  if (!total) return;

  const textNode = (
    <Text variant="body1" ml={2.5} fontSize="14px">
      {commaFormatCurrency(total)}
    </Text>
  );

  if (direction) {
    let tableNode;

    if (direction === 'up') {
      tableNode = (
        <Stack direction="row">
          <Icons.ArrowUpward color="primary" sx={StylesForTableArrow} />
          {textNode}
        </Stack>
      );
    } else {
      tableNode = (
        <Stack direction="row">
          <Icons.ArrowDownward color="error" sx={StylesForTableArrow} />
          {textNode}
        </Stack>
      );
    }
    return tableNode;
  }

  return textNode;
}

function PartnerScore({
  name,
  data,
  borderColor,
  appreciationPrice,
  appreciationDirection,
  disabledAppreciation,
}: PartnersProps) {
  const { isMobile } = useScreenSize();
  // use the last partner object to pull most recent info
  const lastUpdatedItem = data?.[data?.length - 1] || {};
  const getMonthWithLeadingZero =
    `0${lastUpdatedItem?.date?.parsed?.month}`.slice(-2);
  const getYearWithTwoDigits = lastUpdatedItem?.date?.parsed?.year
    ?.toString()
    .slice(-2);
  const lastUpdatedDate =
    lastUpdatedItem?.date?.parsed?.month && lastUpdatedItem?.date?.parsed?.year
      ? `${getMonthWithLeadingZero}/${
          isMobile ? getYearWithTwoDigits : lastUpdatedItem.date.parsed.year
        }`
      : '-';

  return (
    <StyledScoreStack direction="row" justifyContent="space-between">
      <StyledPartnerName variant="h5" sx={{ borderColor }}>
        {name}
      </StyledPartnerName>
      <Stack>
        <Text variant="body1">
          <b>{lastUpdatedItem.score}</b>/100
        </Text>
        <ConfidenceScoreBar>
          <ConfidenceScorePercent
            sx={{ width: `${lastUpdatedItem.score || 0}%` }}
          />
        </ConfidenceScoreBar>
        <Text variant="body1" fontSize="12px">
          confidence score
        </Text>
      </Stack>
      {!disabledAppreciation && (
        <ValueStack>
          <Text>
            {lastUpdatedItem.value
              ? commaFormatCurrency(lastUpdatedItem.value)
              : '-'}
          </Text>
          <Stack direction="row">
            {getTableNodeWithDirection(
              appreciationDirection || '',
              appreciationPrice || 0
            )}
          </Stack>
          <Text variant="body1" fontSize="12px">
            {isMobile ? 'Updated:' : 'Last Updated:'} {lastUpdatedDate}
          </Text>
        </ValueStack>
      )}
    </StyledScoreStack>
  );
}

export function PartnerScoreTable({ data }: PartnersVisualizationProps) {
  const theme = useTheme();
  const defaultLineColors = [
    theme.palette.lineChart.main,
    theme.palette.lineChart.dark,
    theme.palette.lineChart.light,
  ];

  return (
    <>
      {data?.map((partnerList: PartnersProps, index: number) => (
        <PartnerScore
          data={partnerList.data}
          name={partnerList.name}
          appreciationPrice={partnerList.appreciationPrice}
          appreciationDirection={partnerList.appreciationDirection}
          disabledAppreciation={partnerList.disabledAppreciation}
          borderColor={defaultLineColors[index]}
          key={index}
        />
      ))}
    </>
  );
}
